// docs/132: choose my player colour. A swatch in my colour opens a grid of the 24 player colours;
// a colour another player holds (chosen, or theirs by seat) shows their initial and cannot be
// taken; mine is ticked. "Seat colour" lets my choice go. The same button sits on my seat card in
// the World lobby and in the World screen's band panel, where a late joiner forms their band.
import { useState, type CSSProperties } from 'react';
import { Button, Modal } from '../components';
import { useNames, usePlayerColourRows } from '../../core/subscriptions/hooks';
import { useSession } from '../../core/stores/session';
import { identityHex } from '../../core/net/connection';
import { rpc } from '../../core/net/reducers';
import { PLAYER_COLOURS } from '../../game/board/players';
import { playerColour, usePlayerColours } from './ChatDock';
import './colours.css';

/** `choose_colour` with this goes back to the seat's colour (the module's `COLOUR_NONE`). */
const COLOUR_NONE = 255;

export function ColourButton({ worldId, className = '' }: { worldId: bigint; className?: string }) {
  const me = useSession((s) => s.identity) ?? '';
  const colours = usePlayerColours(worldId);
  const mine = colours.get(me);
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={`fk-colour-btn ${className}`} style={{ ['--fk-swatch' as string]: playerColour(mine) } as CSSProperties}
        onClick={() => setOpen(true)} title="Choose your colour" aria-label="Choose your colour" data-testid="colour-open" data-colour={mine} />
      <Modal open={open} onClose={() => setOpen(false)} eyebrow="Your colour" title="Choose your colour" size="narrow" testId="colour-modal">
        <ColourGrid worldId={worldId} onChosen={() => setOpen(false)} />
      </Modal>
    </>
  );
}

export function ColourGrid({ worldId, onChosen }: { worldId: bigint; onChosen?: () => void }) {
  const me = useSession((s) => s.identity) ?? '';
  const colours = usePlayerColours(worldId);
  const chosen = usePlayerColourRows(worldId);
  const names = useNames(worldId);
  const [busy, setBusy] = useState(false);
  const mine = colours.get(me);
  const iChose = chosen.some((c) => identityHex(c.owner) === me);
  const holder = new Map<number, string>();
  for (const [o, n] of colours) if (o !== me) holder.set(n, o);
  const pick = async (colour: number) => {
    setBusy(true);
    const r = await rpc.chooseColour(worldId, colour);
    setBusy(false);
    if (r.ok) onChosen?.();
  };
  return (
    <div className="fk-colours">
      <p className="fk-colours__lede">Your name, seat, map token and plans show in this colour for everyone in the World.</p>
      <div className="fk-colours__grid" role="radiogroup" aria-label="Player colours" data-testid="colour-grid">
        {PLAYER_COLOURS.map((_, i) => {
          const n = i + 1;
          const who = holder.get(n);
          const name = who ? names[who] ?? 'another player' : undefined;
          const on = mine === n;
          return (
            <button key={n} type="button" role="radio" aria-checked={on} className={`fk-colours__swatch ${on ? 'fk-colours__swatch--on' : ''} ${who ? 'fk-colours__swatch--held' : ''}`}
              style={{ ['--fk-swatch' as string]: `var(--fk-p${n})` } as CSSProperties} disabled={busy || !!who || (on && iChose)}
              onClick={() => void pick(i)} title={who ? `${name}'s colour` : on ? 'Your colour' : 'Take this colour'}
              data-testid="colour-swatch" data-colour={n} data-held={who ? 'true' : undefined}>
              {on ? <span aria-hidden="true">✓</span> : who ? <span aria-hidden="true">{(name ?? '?').slice(0, 1).toUpperCase()}</span> : null}
            </button>
          );
        })}
      </div>
      <div className="fk-colours__foot">
        <span className="fk-faint">{iChose ? 'You chose this colour.' : 'Your seat gave you this colour.'}</span>
        {iChose && <Button small disabled={busy} onClick={() => void pick(COLOUR_NONE)} data-testid="colour-seat">Seat colour</Button>}
      </div>
    </div>
  );
}
